/**
 * Leitura do GET /v1/u1/health.
 *
 * O endpoint pertence ao Presence Runtime, que está sendo escrito em paralelo.
 * Por isso este módulo não confia no formato: ele aceita `unknown` e extrai o
 * que reconhece, tolerando snake_case, camelCase e as duas formas de dizer
 * "está configurado" (`configured`, `ok`, `enabled`, `has_key`). O que não
 * reconhecer vira `null` — e `null` aqui significa "o servidor não disse",
 * que é diferente de "está desligado". O painel mostra os três casos com
 * palavras diferentes; fingir certeza sobre o que não sabemos é justamente o
 * que esta tela existe para não fazer.
 */

import { VOICE_MODES, type VoiceMode } from '@will/contracts';

/** ADR-008: mesma origem, API em rota. Nada de host separado. */
export const DEFAULT_API_BASE = '/v1/u1';

export interface DependencyHealth {
  /** true ligado, false sem credencial/endereço, null não informado */
  configured: boolean | null;
  /** modelo, voz ou endereço — o que o servidor quiser expor */
  detail: string | null;
}

export interface U1Health {
  status: 'ok' | 'degraded' | 'down' | 'unknown';
  version: string | null;
  /** modo que o servidor tem configurado (U1_VOICE_MODE); pode divergir do escolhido no painel */
  serverVoiceMode: VoiceMode | null;
  realtime: DependencyHealth;
  omnivoice: DependencyHealth;
  brain: DependencyHealth;
}

export type HealthPhase = 'loading' | 'ok' | 'unreachable';

export interface HealthReading {
  phase: HealthPhase;
  health: U1Health | null;
  /** mensagem crua do erro, para o painel dizer o que aconteceu em vez de só "offline" */
  error: string | null;
  /** epoch ms da última resposta — de sucesso ou de falha */
  checkedAt: number | null;
}

const UNKNOWN_DEP: DependencyHealth = { configured: null, detail: null };

export const UNKNOWN_HEALTH: U1Health = {
  status: 'unknown',
  version: null,
  serverVoiceMode: null,
  realtime: UNKNOWN_DEP,
  omnivoice: UNKNOWN_DEP,
  brain: UNKNOWN_DEP,
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Primeira chave presente vence; as variantes existem porque o formato ainda não está fechado. */
function pick(source: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const k of keys) {
    if (source[k] !== undefined && source[k] !== null) return source[k];
  }
  return undefined;
}

function asString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function asBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  // alguns runtimes serializam flags como 'true'/'false' ou 0/1
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'ok' || s === 'up' || s === 'yes') return true;
    if (s === 'false' || s === 'down' || s === 'off' || s === 'no') return false;
  }
  return null;
}

/**
 * Lê uma dependência aceitando três formatos: objeto aninhado
 * (`{realtime:{configured:true}}`), booleano direto (`{realtime:true}`) e
 * campo achatado (`{realtime_configured:true}`).
 */
function readDependency(root: Record<string, unknown>, name: string): DependencyHealth {
  const nested = pick(root, [name, `${name}_status`]);

  if (isRecord(nested)) {
    return {
      configured: asBool(pick(nested, ['configured', 'ok', 'enabled', 'available', 'has_key', 'hasKey', 'status'])),
      detail: asString(pick(nested, ['detail', 'model', 'voice', 'base_url', 'baseUrl', 'note'])),
    };
  }

  const flat = asBool(nested) ?? asBool(pick(root, [
    `${name}_configured`, `${name}Configured`, `${name}_enabled`, `has_${name}`,
  ]));

  return {
    configured: flat,
    detail: asString(pick(root, [`${name}_model`, `${name}_voice`, `${name}_detail`])),
  };
}

function readStatus(v: unknown): U1Health['status'] {
  const s = asString(v)?.toLowerCase();
  if (s === 'ok' || s === 'up' || s === 'healthy' || s === 'pass') return 'ok';
  if (s === 'degraded' || s === 'warn' || s === 'partial') return 'degraded';
  if (s === 'down' || s === 'fail' || s === 'error') return 'down';
  return 'unknown';
}

function readVoiceMode(v: unknown): VoiceMode | null {
  const s = asString(v);
  return s !== null && (VOICE_MODES as readonly string[]).includes(s) ? (s as VoiceMode) : null;
}

export function parseHealth(payload: unknown): U1Health {
  if (!isRecord(payload)) return UNKNOWN_HEALTH;

  // o corpo pode vir embrulhado em {data:…} ou {health:…}
  const wrapped = pick(payload, ['data', 'health', 'result']);
  const root = isRecord(wrapped) ? wrapped : payload;

  // dependências podem estar num sub-objeto dedicado
  const depsRaw = pick(root, ['dependencies', 'services', 'checks', 'deps']);
  const deps = isRecord(depsRaw) ? depsRaw : root;

  return {
    status: readStatus(pick(root, ['status', 'state'])),
    version: asString(pick(root, ['version', 'build', 'revision'])),
    serverVoiceMode: readVoiceMode(pick(root, ['voice_mode', 'voiceMode', 'mode'])),
    realtime: readDependency(deps, 'realtime'),
    omnivoice: readDependency(deps, 'omnivoice'),
    brain: readDependency(deps, 'brain'),
  };
}

/**
 * Busca o health. Erros viram valor de retorno em vez de exceção: para o painel
 * "não consegui falar com a API" é uma informação a exibir, não um acidente.
 * Só a interrupção por AbortController propaga, para o chamador ignorá-la.
 */
export async function fetchHealth(
  apiBase: string,
  signal: AbortSignal,
): Promise<{ health: U1Health | null; error: string | null }> {
  try {
    const res = await fetch(`${apiBase}/health`, {
      signal,
      headers: { accept: 'application/json' },
      // o painel precisa do estado de agora, não do que um proxy guardou
      cache: 'no-store',
    });

    if (!res.ok) {
      return { health: null, error: `HTTP ${res.status}` };
    }

    return { health: parseHealth(await res.json()), error: null };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    return { health: null, error: err instanceof Error ? err.message : 'falha de rede' };
  }
}
